import { useState, useEffect } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalContent,
  Spinner,
  Tooltip,
  Select,
  SelectItem,
} from "@heroui/react";
import { toast } from "react-toastify";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { EditableInputComponent } from "components/index";
import { BuildingDTO } from "shared/types/building";
import ButtonNew from "components/buttonNew/buttonNew";
import { Close, Save } from "assets/icons";
import { Lightning } from "@phosphor-icons/react";
import { useSession } from "@clerk/clerk-react";

interface BuildingAttributeTemplate {
  id: number;
  category: string;
  ledger: string;
}

interface AttributeData {
  name: string;
  description: string;
}

interface CreateAttributesModalProps {
  isOpen: boolean;
  onClose: () => void;
  building: BuildingDTO;
  onAttributesCreated: (createdCount?: number) => void;
  attributeType: "parkings_inside" | "parkings_outside";
}

const CreateAttributesModal = ({
  isOpen,
  onClose,
  building,
  onAttributesCreated,
  attributeType,
}: CreateAttributesModalProps) => {
  const [attributes, setAttributes] = useState<AttributeData[]>([]);
  const [templates, setTemplates] = useState<BuildingAttributeTemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(
    null
  );
  // Kostenplaats (Odoo analytic account) applied to every attribute in this batch.
  // Prefilled with the building's default parkings kostenplaats but editable, mirroring the
  // single-attribute creation flow where revenue is booked against this analytic account.
  const [kostenplaats, setKostenplaats] = useState<string>("");
  const { session } = useSession();
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);

  // Get the count based on attribute type
  const getAttributeCount = () => {
    if (!building?.buildingAcquisitionInfo) return 0;
    if (attributeType === "parkings_inside") {
      return building.buildingAcquisitionInfo.acquisitionFileNumParkingsInside || 0;
    }
    return building.buildingAcquisitionInfo.acquisitionFileNumParkingsOutside || 0;
  };

  // Get display name for the attribute type
  const getAttributeTypeLabel = () => {
    if (attributeType === "parkings_inside") {
      return "parkeerplaatsen binnen";
    }
    return "parkeerplaatsen buiten";
  };

  // Get default prefix for naming
  const getDefaultPrefix = () => {
    if (attributeType === "parkings_inside") {
      return "Garage";
    }
    return "Parking";
  };

  // Get the selected template object
  const getSelectedTemplate = (): BuildingAttributeTemplate | null => {
    if (!selectedTemplateId) return null;
    return templates.find((t) => t.id === selectedTemplateId) || null;
  };

  // Get prefix for naming based on selected template
  const getNamePrefix = (): string => {
    const template = getSelectedTemplate();
    if (template) {
      return template.category;
    }
    // Fallback to default prefix if no template selected
    return getDefaultPrefix();
  };

  // Fetch templates on mount
  useEffect(() => {
    const fetchTemplates = async () => {
      setIsLoadingTemplates(true);
      try {
        const response = await fetchWithAuth(
          `${API_URL}/clusters/templates`,
          {
            method: "GET",
            headers: { "Content-Type": "application/json" },
          },
          session
        );
        if (response.ok) {
          const data = await response.json();
          setTemplates(Array.isArray(data) ? data : []);
          // Don't auto-select any template - user must choose
        }
      } catch (error) {
        console.error("Error fetching templates:", error);
        toast.error("Fout bij ophalen van templates");
      } finally {
        setIsLoadingTemplates(false);
      }
    };

    if (isOpen) {
      fetchTemplates();
    }
  }, [isOpen, session]);

  // Initialize attributes when modal opens (with empty names until template is selected)
  useEffect(() => {
    if (isOpen && building) {
      const count = getAttributeCount();
      const initialAttributes: AttributeData[] = [];

      for (let i = 0; i < count; i++) {
        initialAttributes.push({
          name: "", // Empty until template is selected
          description: "",
        });
      }

      setAttributes(initialAttributes);

      // Prefill the kostenplaats with the building's default parkings analytic account.
      // This matches the rental fallback ("<buildingId>-PARKINGS") so created attributes are
      // booked against the right kostenplaats out of the box, while remaining editable.
      setKostenplaats(building.id ? `${building.id}-PARKINGS` : "");
    }
  }, [building, isOpen, attributeType]);

  // When template is selected, regenerate all names based on template category
  useEffect(() => {
    if (selectedTemplateId && attributes.length > 0) {
      const template = templates.find((t) => t.id === selectedTemplateId);
      if (template) {
        const updatedAttributes = attributes.map((attr, index) => ({
          ...attr,
          name: `${template.category} ${index + 1}`,
        }));
        setAttributes(updatedAttributes);
      }
    }
  }, [selectedTemplateId, templates]);

  // Clear state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setAttributes([]);
      setSelectedTemplateId(null);
      setKostenplaats("");
    }
  }, [isOpen]);

  const handleAttributeChange = (
    index: number,
    field: keyof AttributeData,
    value: string
  ) => {
    const updatedAttributes = [...attributes];
    updatedAttributes[index][field] = value;
    setAttributes(updatedAttributes);
  };

  const isFormValid = () => {
    return (
      selectedTemplateId !== null &&
      attributes.every((attr) => attr.name && attr.name.trim() !== "")
    );
  };

  const generateNames = (pattern: "number" | "letter" | "g-number") => {
    const prefix = getNamePrefix();
    const updatedAttributes = attributes.map((attr, index) => {
      let name = "";
      switch (pattern) {
        case "number":
          name = `${prefix} ${index + 1}`;
          break;
        case "letter":
          name = `${prefix} ${String.fromCharCode(65 + index)}`; // A, B, C...
          break;
        case "g-number":
          name = `G${index + 1}`;
          break;
      }
      return { ...attr, name };
    });
    setAttributes(updatedAttributes);
    toast.success(`Namen gegenereerd`);
  };

  const generateSingleName = (index: number) => {
    const prefix = getNamePrefix();
    const updatedAttributes = [...attributes];
    updatedAttributes[index] = {
      ...updatedAttributes[index],
      name: `${prefix} ${index + 1}`,
    };
    setAttributes(updatedAttributes);
  };

  const handleSubmit = async () => {
    if (!isFormValid()) {
      toast.error(
        "Selecteer een template en zorg ervoor dat alle attributen een naam hebben."
      );
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetchWithAuth(
        `${API_URL}/building-attributes/quick-create`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            buildingId: building.id,
            templateId: selectedTemplateId,
            // Batch-wide kostenplaats; empty becomes null server-side (no analytic distribution).
            odooAnalyticAccountName: kostenplaats.trim() || null,
            attributes: attributes.map((attr) => ({
              name: attr.name,
              description: attr.description || "",
            })),
          }),
        },
        session
      );

      if (response.ok) {
        const result = await response.json();
        const successCount = result.success?.length || 0;
        const errorCount = result.errors?.length || 0;

        if (errorCount > 0) {
          toast.warning(
            `${successCount} attributen aangemaakt, ${errorCount} fouten.`
          );
        } else {
          toast.success(`${successCount} attributen succesvol aangemaakt.`);
        }

        onAttributesCreated(successCount);
        onClose();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || "Fout bij aanmaken van attributen.");
      }
    } catch (error: any) {
      console.error("Failed to create attributes:", error);
      toast.error(error.message || "Fout bij aanmaken van attributen.");
    } finally {
      setIsLoading(false);
    }
  };

  const attributeCount = getAttributeCount();

  return (
    <Modal scrollBehavior="inside" isOpen={isOpen} onClose={!isLoading ? onClose : undefined} size="full">
      <ModalHeader>
        Maak {getAttributeTypeLabel()} aan voor {building?.id}
      </ModalHeader>
      <ModalContent>
        <ModalBody>
          <div className="flex justify-between items-start gap-4 p-4">
            <div className="flex flex-1 flex-wrap items-start gap-4">
              <div className="w-full max-w-xs">
                {isLoadingTemplates ? (
                  <Spinner size="sm" />
                ) : (
                  <div className="flex flex-col gap-1">
                    <Select
                      label="Selecteer template"
                      placeholder="Kies een attribuut template"
                      selectedKeys={
                        selectedTemplateId ? [selectedTemplateId.toString()] : []
                      }
                      onSelectionChange={(keys) => {
                        const key = Array.from(keys)[0];
                        setSelectedTemplateId(key ? Number(key) : null);
                      }}
                      isDisabled={isLoading}
                      isRequired
                    >
                      {templates.map((template) => (
                        <SelectItem key={template.id.toString()}>
                          {template.category}
                        </SelectItem>
                      ))}
                    </Select>
                    {!selectedTemplateId && (
                      <p className="text-xs text-amber-600">
                        Selecteer eerst een template om namen te genereren
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Kostenplaats (Odoo analytic account) applied to all created attributes */}
              <div className="w-full max-w-xs">
                <EditableInputComponent
                  label="Kostenplaats (Odoo)"
                  editMode={true}
                  isLoading={false}
                  value={kostenplaats}
                  onChange={(value: string) => setKostenplaats(value)}
                />
                <p className="text-xs text-neutral-secondary-mid mt-1">
                  Wordt toegepast op alle aangemaakte attributen. Laat leeg voor
                  geen analytische verdeling.
                </p>
              </div>
            </div>

            <div className="flex gap-2">
              {isLoading ? (
                <Spinner
                  size="md"
                  label="Attributen aan het aanmaken, sluit de modal niet!"
                />
              ) : (
                <>
                  <ButtonNew
                    icon={<Save />}
                    label="Opslaan"
                    variant="primaryMoss"
                    onClick={handleSubmit}
                    isDisabled={!isFormValid()}
                  />
                  <ButtonNew
                    icon={<Close />}
                    label="Sluiten"
                    variant="primaryBrick"
                    onClick={onClose}
                  />
                </>
              )}
            </div>
          </div>

          {attributeCount === 0 ? (
            <div className="p-8 text-center text-neutral-secondary-mid">
              <p>
                Er zijn geen {getAttributeTypeLabel()} ingesteld voor dit gebouw.
              </p>
              <p className="text-sm mt-2">
                Stel eerst het aantal {getAttributeTypeLabel()} in op het
                overzichtsscherm.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <div className="mb-4">
                <h2 className="text-lg font-medium">
                  Maak mappen aan in SharePoint voor {getAttributeTypeLabel()}
                  <span className="ml-2 text-sm font-normal text-neutral-secondary-mid">
                    ({attributes.length}{" "}
                    {attributes.length === 1 ? "attribuut" : "attributen"})
                  </span>
                </h2>
              </div>

              <table className="w-full text-left text-sm">
                <thead className="bg-gray-50 text-xs uppercase">
                  <tr>
                    <th scope="col" className="px-3 py-3 text-center w-12">
                      #
                    </th>
                    <th scope="col" className="px-6 py-3">
                      <div className="flex items-center gap-2">
                        <span>Naam</span>
                        <div className="flex gap-1">
                          <Tooltip content={`Genereer: ${getNamePrefix()} 1, ${getNamePrefix()} 2, ...`}>
                            <button
                              onClick={() => generateNames("number")}
                              disabled={isLoading || attributes.length === 0 || !selectedTemplateId}
                              className="p-1 rounded-md hover:bg-moss-primary/20 text-moss-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              type="button"
                            >
                              <Lightning size={14} weight="fill" />
                            </button>
                          </Tooltip>
                          <Tooltip content="Genereer: G1, G2, G3, ...">
                            <button
                              onClick={() => generateNames("g-number")}
                              disabled={isLoading || attributes.length === 0 || !selectedTemplateId}
                              className="px-1.5 py-0.5 text-xs rounded-md hover:bg-teal-500/20 text-teal-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                              type="button"
                            >
                              G#
                            </button>
                          </Tooltip>
                          <Tooltip content={`Genereer: ${getNamePrefix()} A, ${getNamePrefix()} B, ...`}>
                            <button
                              onClick={() => generateNames("letter")}
                              disabled={isLoading || attributes.length === 0 || !selectedTemplateId}
                              className="px-1.5 py-0.5 text-xs rounded-md hover:bg-blue-500/20 text-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                              type="button"
                            >
                              ABC
                            </button>
                          </Tooltip>
                        </div>
                      </div>
                    </th>
                    <th scope="col" className="px-6 py-3">
                      Beschrijving (optioneel)
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {attributes.map((attribute, index) => (
                    <tr key={index} className="gap-2 border-b hover:bg-gray-50">
                      <td className="px-3 py-4 text-center">
                        <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-teal-500/10 text-teal-600 font-medium text-xs">
                          {index + 1}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <EditableInputComponent
                            label="Naam"
                            editMode={true}
                            isLoading={false}
                            value={attribute.name}
                            onChange={(value: string) =>
                              handleAttributeChange(index, "name", value)
                            }
                          />
                          <Tooltip
                            content={`Genereer: ${getNamePrefix()} ${index + 1}`}
                          >
                            <button
                              onClick={() => generateSingleName(index)}
                              disabled={!selectedTemplateId}
                              className="p-1.5 rounded-md hover:bg-moss-primary/10 text-neutral-secondary-mid hover:text-moss-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              type="button"
                            >
                              <Lightning size={16} weight="fill" />
                            </button>
                          </Tooltip>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <EditableInputComponent
                          label="Beschrijving"
                          editMode={true}
                          isLoading={false}
                          value={attribute.description}
                          onChange={(value: string) =>
                            handleAttributeChange(index, "description", value)
                          }
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default CreateAttributesModal;
